import { useEffect, useState } from 'react';

/**
 * Rob's voice for the team (docs/plans/voice-ab.md, "Decided by Alexi, Oct 17"): a signed-in
 * owner, admin or officer may hear either voice, anytime, outside the voice test. Visitors get
 * whatever the test gives them, and their browser never learns which.
 *
 * Who is on the team is decided on the server, by role, on every request for Rob's voice
 * (functions/speech.js); a `voice` from anyone else is ignored there. This module only decides
 * whether the pick is shown (Sidebar) and sent (utils/voice.js):
 *   - the role is what getMyRole said (App.js), never a mark the browser keeps
 *   - the pick lives in localStorage apr-team-voice: "grok" (the default) or "elevenlabs"
 */

export const TEAM_VOICES = ['grok', 'elevenlabs'];
export const VOICE_NAMES = { grok: 'Grok Rob', elevenlabs: 'ElevenLabs Rob' };

/**
 * The team's note when ElevenLabs was picked and Grok read instead (the X-Voice-Fallback header,
 * which only a team member's requests get), word for word from the voice A/B contract (§4.4).
 */
export const FALLBACK_NOTES = {
  error: "ElevenLabs had an error; Grok read this part.",
  timeout: "ElevenLabs was too slow; Grok read this part.",
  guard: "ElevenLabs only reads what it can check AI Rob said; Grok read this part.",
  nokey: "There's no ElevenLabs key on the server; Grok read this.",
  backstop: "You've used today's ElevenLabs allowance; Grok read this.",
};
const TEAM_ROLES = ['owner', 'admin', 'officer'];
const KEY = 'apr-team-voice';

let role = null;
let picked = null; // this visit's pick when storage is blocked
const listeners = new Set();

function notify() {
  const value = teamVoicePick();
  listeners.forEach((fn) => { try { fn(value); } catch (e) { /* a listener never breaks the pick */ } });
}

export function isTeamRole(value) {
  return TEAM_ROLES.includes(value);
}

/** getMyRole's answer (App.js), or null when signed out or not on the team. */
export function setTeamRole(next) {
  const value = isTeamRole(next) ? next : null;
  if (value === role) return;
  role = value;
  notify();
}

function readPick() {
  try {
    const saved = localStorage.getItem(KEY);
    if (TEAM_VOICES.includes(saved)) return saved;
  } catch (e) { /* storage blocked: this visit's pick */ }
  return picked || 'grok';
}

/** The voice a team member picked ("grok" unless they chose otherwise), or null for anyone else. */
export function teamVoicePick() {
  return role ? readPick() : null;
}

/**
 * Whether getMyRole named this person owner, admin or officer (subscribeTeamVoice hears a change):
 * the team's other pick, First Answer's (utils/experiments.js), shows and goes only then.
 */
export function onTeam() {
  return Boolean(role);
}

export function setTeamVoice(voice) {
  if (!TEAM_VOICES.includes(voice)) return;
  picked = voice;
  try { localStorage.setItem(KEY, voice); } catch (e) { /* storage blocked: this visit only */ }
  notify();
}

export function subscribeTeamVoice(fn) {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** The pick for a component: null for a visitor, else "grok" or "elevenlabs". */
export function useTeamVoice() {
  const [voice, setVoice] = useState(teamVoicePick);
  useEffect(() => {
    setVoice(teamVoicePick());
    return subscribeTeamVoice(setVoice);
  }, []);
  return voice;
}

/**
 * The phone harness's stand-in for getMyRole (scripts/phone/voice-ab.js sets
 * window.__APR_ROLE_TEST__), honored only on a local address, which never reaches production's
 * roles; like __APR_TRACK_TEST__ in utils/analytics.js. Undefined everywhere else, and then the
 * real answer decides. It changes what this page shows, never what the server allows.
 */
export function localTestRole() {
  try {
    if (typeof window === 'undefined' || !('__APR_ROLE_TEST__' in window)) return undefined;
    const host = window.location.hostname;
    if (host !== 'localhost' && host !== '127.0.0.1' && host !== '[::1]') return undefined;
    return isTeamRole(window.__APR_ROLE_TEST__) ? window.__APR_ROLE_TEST__ : null;
  } catch (e) {
    return undefined;
  }
}
